import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, TrendingUp } from "lucide-react";
import { FilterPills } from "@/components/FilterPills";
import { LeadCard } from "@/components/collector/LeadCard";
import { CollectorAvatar } from "@/components/CollectorAvatar";

// Time-sensitive first: reopened second-chance leads, then new, then claimed, then blocked.
const leadPriority = (l) => {
  if (l.status === "requested" && l.reopened) return 0;
  if (l.status === "requested") return 1;
  if (l.status === "contacted") return 2;
  if (l.status === "taken") return 3;
  return 4;
};

const byPriority = (a, b) => leadPriority(a) - leadPriority(b);

// New leads first, longest-waiting customer at the top; everything else keeps priority order.
const byLongestWait = (a, b) => {
  const an = a.status === "requested", bn = b.status === "requested";
  if (an && bn) return (a.created_at || "").localeCompare(b.created_at || "");
  return an ? -1 : bn ? 1 : byPriority(a, b);
};

const SortSelect = ({ value, onChange }) => (
  <select data-testid="leads-sort-select" aria-label="Sort leads" className="input-soft sm:!w-auto text-sm !py-2" value={value} onChange={(e) => onChange(e.target.value)}>
    <option value="priority">Sort: Priority</option>
    <option value="waiting">Sort: Longest waiting</option>
  </select>
);

const groupByCollector = (leads) => {
  const groups = {};
  leads.forEach((l) => {
    if (!groups[l.collector_id]) groups[l.collector_id] = { id: l.collector_id, name: l.collector_name || "Collector", photo: l.collector_photo_url, leads: [] };
    groups[l.collector_id].leads.push(l);
  });
  return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
};

const CardGrid = ({ leads, onChanged, showCollector, canReassign }) => (
  <ul className="space-y-4 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-4 sm:space-y-0">
    {leads.map((l) => <LeadCard key={l.id} l={l} onChanged={onChanged} showCollector={showCollector} canReassign={canReassign} />)}
  </ul>
);

const GroupedLeads = ({ leads, onChanged, canReassign }) => (
  <div className="space-y-8" data-testid="leads-grouped">
    {groupByCollector(leads).map((g) => (
      <section key={g.id} data-testid={`leads-group-${g.id}`}>
        <Link to={`/dashboard/company/collectors/${g.id}`} className="flex items-center gap-2.5 mb-3 pb-2 border-b border-[#1E3F33]/10 hover:text-[#2D6A4F]" data-testid={`leads-group-header-${g.id}`}>
          <CollectorAvatar name={g.name} photo={g.photo} size="w-8 h-8" />
          <span className="font-heading text-base text-[#1E3F33]">{g.name}</span>
          <span className="text-xs text-[#8A9A86]">{g.leads.length} lead{g.leads.length === 1 ? "" : "s"}</span>
        </Link>
        <CardGrid leads={g.leads} onChanged={onChanged} showCollector={false} canReassign={canReassign} />
      </section>
    ))}
  </div>
);

export const LeadsBoard = ({
  leads, onChanged, showCollector = false, showInsights = false, toolbar = null, groupable = false, canReassign = false,
  sortable = false, title = "Customer leads",
  subtitle = "New customer requests in your service area. Contact them, then mark \"I contacted this customer\".",
  testid = "incoming-requests-card",
}) => {
  const [view, setView] = useState("open");
  const [grouped, setGrouped] = useState(false);
  const [sort, setSort] = useState("priority");
  const isDone = (l) => l.status === "confirmed";
  const sortFn = sortable && view === "open" && sort === "waiting" ? byLongestWait : byPriority;
  const shown = leads.filter((l) => (view === "completed" ? isDone(l) : !isDone(l))).sort(sortFn);

  return (
    <div className="card-soft mb-6" data-testid={testid}>
      <h2 className="font-heading text-xl text-[#1E3F33] mb-3 flex items-center gap-2"><Inbox className="w-4 h-4 text-[#CC5A3A]"/> {title}</h2>
      <p className="text-xs text-[#8A9A86] mb-3">{subtitle}</p>
      {showInsights && (
        <Link to="/dashboard/leads/insights" data-testid="lead-insights-link" className="inline-flex items-center gap-1.5 text-sm text-[#CC5A3A] font-medium hover:underline underline-offset-2 mb-4">
          <TrendingUp className="w-4 h-4"/> View lead insights
        </Link>
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <FilterPills
          options={[
            { key: "open", label: `Open (${leads.filter((l) => !isDone(l)).length})` },
            { key: "completed", label: `Completed (${leads.filter(isDone).length})` },
          ]}
          value={view}
          onChange={setView}
          testidPrefix="collector-leads-view"
        />
        <div className="flex items-center gap-3 flex-wrap">
          {groupable && (
            <label className="inline-flex items-center gap-2 text-sm text-[#5C6B64] cursor-pointer select-none">
              <input type="checkbox" data-testid="leads-group-toggle" checked={grouped} onChange={(e) => setGrouped(e.target.checked)} className="rounded border-[#1E3F33]/30 text-[#2D6A4F]" />
              Group by collector
            </label>
          )}
          {sortable && view === "open" && <SortSelect value={sort} onChange={setSort} />}
          {toolbar}
        </div>
      </div>
      <div className="mt-4" />
      {shown.length === 0 ? (
        <p className="text-sm text-[#8A9A86]" data-testid="leads-empty">{view === "completed" ? "No completed requests yet." : "No new leads yet."}</p>
      ) : groupable && grouped ? <GroupedLeads leads={shown} onChanged={onChanged} canReassign={canReassign} />
        : <CardGrid leads={shown} onChanged={onChanged} showCollector={showCollector} canReassign={canReassign} />}
    </div>
  );
};
